import { useEffect, useMemo, useState } from 'react';
import { Link } from '@/components/LocalizedLink';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  COOKIE_CATEGORY_DEFINITIONS,
  COOKIE_POLICY_PATH,
  getDefaultCookiePreferences,
  onCookiePreferencesOpen,
  persistCookieConsent,
  readCookieConsent,
  shouldShowCookieConsentBanner,
  withAllNonEssentialEnabled,
  type CookiePreferences,
} from '@/lib/cookieConsent';

const CookieConsentBanner = () => {
  const { lang } = useLanguage();
  const [ready, setReady] = useState(false);
  const [visible, setVisible] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [draftPreferences, setDraftPreferences] = useState<CookiePreferences>(getDefaultCookiePreferences());

  useEffect(() => {
    const stored = readCookieConsent();
    if (stored) {
      setDraftPreferences(stored.preferences);
      setVisible(false);
    } else {
      setDraftPreferences(getDefaultCookiePreferences());
      setVisible(shouldShowCookieConsentBanner());
    }
    setReady(true);
  }, []);

  useEffect(
    () =>
      onCookiePreferencesOpen(() => {
        setDraftPreferences(readCookieConsent()?.preferences ?? getDefaultCookiePreferences());
        setPreferencesOpen(true);
      }),
    [],
  );

  const copy = useMemo(() => {
    if (lang === 'tr') {
      return {
        title: 'Çerez tercihleri',
        description:
          'Sitemizin güvenli çalışması için zorunlu çerezler kullanırız. Analitik, fonksiyonel ve pazarlama çerezlerini tercihinize göre açabilirsiniz.',
        acceptAll: 'Tümünü Kabul Et',
        rejectNonEssential: 'Zorunlu Hariç Reddet',
        customize: 'Tercihleri Yönet',
        savePreferences: 'Tercihleri Kaydet',
        policy: 'Çerez Politikası',
        preferencesTitle: 'Çerez tercihlerinizi yönetin',
        preferencesDescription:
          'Bu tercih ekranı teknik uygulama amaçlıdır ve hukuki metinler için nihai yasal danışmanlık yerine geçmez. İçerik hukuki incelemeye tabidir.',
      };
    }

    return {
      title: 'Cookie preferences',
      description:
        'We use essential cookies to keep the site secure and functional. You can enable analytics, functional, and marketing cookies based on your preference.',
      acceptAll: 'Accept All',
      rejectNonEssential: 'Reject Non-Essential',
      customize: 'Customize Preferences',
      savePreferences: 'Save Preferences',
      policy: 'Cookie Policy',
      preferencesTitle: 'Manage your cookie preferences',
      preferencesDescription:
        'This preference panel is implemented for technical compliance workflow and does not replace formal legal advice. Text is subject to legal review.',
    };
  }, [lang]);

  const closeBanner = () => {
    setVisible(false);
    setPreferencesOpen(false);
  };

  const onAcceptAll = () => {
    const allEnabled = withAllNonEssentialEnabled();
    persistCookieConsent({ source: 'accept_all', preferences: allEnabled });
    setDraftPreferences(allEnabled);
    closeBanner();
  };

  const onRejectNonEssential = () => {
    const essentialsOnly = getDefaultCookiePreferences();
    persistCookieConsent({ source: 'reject_non_essential', preferences: essentialsOnly });
    setDraftPreferences(essentialsOnly);
    closeBanner();
  };

  const onSaveCustomPreferences = () => {
    const preferences = {
      essential: true as const,
      analytics: draftPreferences.analytics,
      functional: draftPreferences.functional,
      marketing: draftPreferences.marketing,
    };
    persistCookieConsent({
      source: 'customize',
      preferences,
    });
    closeBanner();
  };

  const onToggle = (key: keyof Omit<CookiePreferences, 'essential'>, checked: boolean) => {
    setDraftPreferences((prev) => ({
      ...prev,
      [key]: checked,
    }));
  };

  if (!ready) return null;

  return (
    <>
      {visible && (
      <section
        aria-label={copy.title}
        className="fixed inset-x-0 bottom-4 z-40 px-4 sm:px-6"
      >
        <div className="mx-auto max-w-5xl rounded-2xl border border-border/70 bg-background/95 backdrop-blur-xl shadow-2xl">
          <div className="flex flex-col gap-4 p-4 sm:p-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <h2 className="font-display text-lg text-foreground">{copy.title}</h2>
              <p className="mt-1 font-body text-sm leading-relaxed text-muted-foreground">{copy.description}</p>
              <Link
                to={COOKIE_POLICY_PATH}
                className="mt-2 inline-flex text-xs font-body text-muted-foreground underline underline-offset-4 hover:text-foreground"
              >
                {copy.policy}
              </Link>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onRejectNonEssential}
                className="inline-flex items-center justify-center rounded-full border border-border px-4 py-2 text-xs font-body text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {copy.rejectNonEssential}
              </button>
              <button
                type="button"
                onClick={() => setPreferencesOpen(true)}
                className="inline-flex items-center justify-center rounded-full border border-border px-4 py-2 text-xs font-body text-foreground hover:bg-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {copy.customize}
              </button>
              <button
                type="button"
                onClick={onAcceptAll}
                className="inline-flex items-center justify-center rounded-full bg-foreground px-4 py-2 text-xs font-body text-background hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {copy.acceptAll}
              </button>
            </div>
          </div>
        </div>
      </section>
      )}

      <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
        <DialogContent className="max-w-xl rounded-2xl border border-border/70 bg-background p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="font-display text-2xl text-foreground">{copy.preferencesTitle}</DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground leading-relaxed">
              {copy.preferencesDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-6 pt-2 space-y-4">
            {COOKIE_CATEGORY_DEFINITIONS.map((category) => {
              const checked = category.key === 'essential' ? true : draftPreferences[category.key];
              return (
                <article key={category.key} className="rounded-xl border border-border/70 bg-card/40 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-lg text-foreground">{category.title[lang]}</h3>
                      <p className="mt-1 font-body text-sm text-muted-foreground">{category.description[lang]}</p>
                    </div>
                    <Switch
                      checked={checked}
                      disabled={category.required}
                      onCheckedChange={(value) => {
                        if (category.required) return;
                        onToggle(category.key, value);
                      }}
                      aria-label={category.title[lang]}
                    />
                  </div>
                </article>
              );
            })}

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <Link
                to={COOKIE_POLICY_PATH}
                className="text-xs font-body text-muted-foreground underline underline-offset-4 hover:text-foreground"
                onClick={() => setPreferencesOpen(false)}
              >
                {copy.policy}
              </Link>
              <button
                type="button"
                onClick={onSaveCustomPreferences}
                className="inline-flex rounded-full bg-foreground px-5 py-2.5 text-xs font-body text-background hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {copy.savePreferences}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
